// ============================================================
// manufacturing.nav.ts — Manufacturing hub descriptor
// ------------------------------------------------------------
// Component-free, data-only navigation descriptor for the Manufacturing
// hub. Imports ONLY pure types/constants (@twist/ui-navigation types +
// @twist/core security primitives) so the registry stays a pure data graph.
//
// Gated by the organization features instead of the Twist admin org type:
// a subcontractor with the Manufacturing feature sees its own production;
// the definitions themselves (components, devices, firmware) live under
// Admin › Definitions. The constraints mirror the route guards in app.routes.ts and
// @twist/feature-manufacturing so nav-visible ⊆ route-accessible.
// ============================================================
import { OrgPermissions } from '@twist/core';
import type { NavDescriptor } from '@twist/ui-navigation';

/** Manufacturing feature (own production) or Manufacturing management (everything). */
const MANUFACTURING_ACCESS = {
  requireOrganization: true,
  orgPermissions: [OrgPermissions.MyOrganization__Manufacturing_Read, OrgPermissions.ADMIN__Manufacturing_Read],
};

export const manufacturingNav: NavDescriptor = {
  id: 'manufacturing',
  path: '/manufacturing',
  label: 'Manufacturing hub',
  icon: 'gearsDatabase',
  group: 'Organization',
  // Organization section = 20s; Manufacturing after the organization's own security.
  order: 24,
  surfaces: ['sidebar', 'palette'],
  security: MANUFACTURING_ACCESS,
  children: [
    {
      id: 'manufacturing-devices',
      path: '/manufacturing',
      label: 'Production batches',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_ACCESS,
    },
    {
      id: 'manufacturing-component-orders',
      path: '/manufacturing/component-orders',
      label: 'Component orders',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_ACCESS,
    },
    {
      id: 'manufacturing-packages',
      path: '/manufacturing/packages',
      label: 'Packages',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_ACCESS,
    },
    {
      id: 'manufacturing-stock',
      path: '/manufacturing/stock',
      label: 'Component stock',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_ACCESS,
    },
    {
      id: 'manufacturing-settings',
      path: '/manufacturing/settings',
      label: 'Manufacturing settings',
      group: 'Organization',
      surfaces: ['sidebar', 'palette'],
      security: MANUFACTURING_ACCESS,
    },
  ],
};
